import { resolveOptionDisplayLabel } from '../../utils/config-localization';

/**
 * Nome campo react-hook-form per path annidati con chiavi che contengono spazi.
 * Formato: `merged.crash[Crash type]` (senza virgolette nei bracket).
 *
 * @param {string} prefix
 * @param {string[]} pathParts
 */
export function buildMergeRegisterName(prefix, pathParts = []) {
  if (!pathParts.length) return prefix;
  const [first, ...rest] = pathParts;
  let name = `${prefix}.${first}`;
  rest.forEach((part) => {
    name += `[${part}]`;
  });
  return name;
}

/**
 * @param {unknown} value
 */
export function toSelectFormString(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
}

/**
 * @param {unknown} rawValue
 * @param {{ options?: Array<{ value: unknown, label?: string }> } | undefined} configField
 */
export function resolveSelectFormValue(rawValue, configField) {
  if (!configField?.options?.length) {
    return toSelectFormString(rawValue);
  }

  const matchByValue = configField.options.find(
    (opt) =>
      opt?.value === rawValue ||
      String(opt?.value) === String(rawValue),
  );
  if (matchByValue) return toSelectFormString(matchByValue.value);

  if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
    const raw = String(rawValue).trim().toLowerCase();
    const matchByLabel = configField.options.find((opt) => {
      const label = String(resolveOptionDisplayLabel(opt) || opt?.label || '').trim().toLowerCase();
      return label === raw;
    });
    if (matchByLabel) return toSelectFormString(matchByLabel.value);
    return toSelectFormString(rawValue);
  }

  const unknown = configField.options.find(
    (opt) =>
      String(opt?.value) === '0' ||
      /unknown/i.test(String(opt?.label ?? '')),
  );
  return toSelectFormString(unknown?.value ?? configField.options[0]?.value ?? '');
}

/**
 * @param {{ options?: Array<{ value: unknown, label?: string }> } | undefined} configField
 * @param {(value: unknown, fallbackLabel?: string, optionItem?: object) => string} formatOptionLabel
 * @param {unknown} [rawValue]
 */
export function buildConfigSelectOptions(configField, formatOptionLabel, rawValue) {
  if (configField?.options?.length) {
    return configField.options.map((opt) => ({
      ...opt,
      value: toSelectFormString(opt?.value),
      label: formatOptionLabel(opt?.value, opt?.label, opt),
    }));
  }

  if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
    return [{
      value: toSelectFormString(rawValue),
      label: formatOptionLabel(rawValue, String(rawValue)),
    }];
  }

  return [];
}

/**
 * Ripristina i tipi originali delle option (es. number) prima del salvataggio API.
 * @param {Record<string, unknown>} data
 * @param {Record<string, { options?: Array<{ value: unknown }> }>} configurationMap
 */
export function restoreConfigSelectTypes(data, configurationMap) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

  const restored = { ...data };
  Object.keys(restored).forEach((key) => {
    const value = restored[key];
    if (value && typeof value === 'object') return;

    const configField = configurationMap?.[key];
    if (!configField?.options?.length || value === '' || value == null) return;

    const matched = configField.options.find(
      (opt) => String(opt?.value) === String(value),
    );
    if (matched) {
      restored[key] = matched.value;
    }
  });
  return restored;
}

/**
 * @param {Record<string, unknown>} merged
 * @param {Record<string, { options?: Array<{ value: unknown }> }>} configurationMap
 */
export function normalizeMergedForForm(merged, configurationMap) {
  if (!merged || typeof merged !== 'object') return merged;

  const sections = ['crash', 'road', 'unit', 'person'];
  const next = { ...merged };

  sections.forEach((sectionKey) => {
    if (!next[sectionKey] || typeof next[sectionKey] !== 'object') return;

    const section = { ...next[sectionKey] };
    Object.keys(section).forEach((fieldKey) => {
      const configField = configurationMap?.[fieldKey];
      if (configField?.options?.length) {
        section[fieldKey] = resolveSelectFormValue(section[fieldKey], configField);
      }
    });
    next[sectionKey] = section;
  });

  return next;
}

/**
 * @param {Record<string, unknown>} merged
 * @param {Record<string, { options?: Array<{ value: unknown }> }>} configurationMap
 */
export function restoreMergedPayloadTypes(merged, configurationMap) {
  if (!merged || typeof merged !== 'object') return merged;

  const sections = ['crash', 'road', 'unit', 'person'];
  const next = { ...merged };

  sections.forEach((sectionKey) => {
    if (next[sectionKey] && typeof next[sectionKey] === 'object') {
      next[sectionKey] = restoreConfigSelectTypes(next[sectionKey], configurationMap);
    }
  });

  return next;
}
